import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useAuth0Login } from "~/hooks/useAuth0Login";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "~/constants/queryKeys";
import { apiClient } from "~/lib/orpc-client";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { Spinner } from "~/components/ui/spinner";
import { AccessRestricted } from "~/components/how-to-use/access-restricted";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/_protected/apply/how-to-use/")({
  component: RouteComponent,
});

function formatCertificationLabel(app: {
  category: string | null;
  title: string | null;
}) {
  if (app.category === "WRITER") return "Writer";
  if (app.category === "ARTIST") return app.title || "Artist";
  return app.title || "Untitled";
}

function RouteComponent() {
  const { isAuthenticated, isLoading } = useAuth0Login();
  const { isPublisherAccount } = useAuthenticatedUser();

  const { data: applicationsResponse, isLoading: isLoadingApplications } =
    useQuery({
      queryKey: QUERY_KEYS.APPLICATIONS_LIST,
      queryFn: () => apiClient.applications.list({}),
      enabled: isAuthenticated,
    });

  if (isLoading || isLoadingApplications) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <Spinner className="size-8 text-primary" />
      </div>
    );
  }

  const approvedApps =
    applicationsResponse?.applications?.filter(
      (app) => app.applicationStatus === "APPROVED" && app.certificationNumber,
    ) ?? [];

  const hasAccess = isPublisherAccount || approvedApps.length > 0;

  if (!hasAccess) {
    return <AccessRestricted />;
  }

  // Single certification — redirect directly
  if (approvedApps.length === 1 && approvedApps[0]?.certificationNumber) {
    return (
      <Navigate
        to="/apply/how-to-use/$certificationNumber"
        params={{
          certificationNumber: approvedApps[0]?.certificationNumber ?? 0,
        }}
        replace
      />
    );
  }

  // Multiple certifications — show a list
  return (
    <div className="bg-primary/5">
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">
            Your Certifications
          </h1>
          <p className="text-sm text-foreground mt-1">
            Select a certification to view its usage guide and QR code.
          </p>
        </div>

        <div className="space-y-3">
          {approvedApps.map((app) => (
            <Link
              key={app.id}
              to="/apply/how-to-use/$certificationNumber"
              params={{
                certificationNumber: app.certificationNumber!,
              }}
              className="block bg-white rounded-lg border border-primary/20 p-4 hover:border-primary/40 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-black">
                    {formatCertificationLabel(app)}
                  </h3>
                  <p className="text-sm text-black mt-1">
                    Certification: {app.certificationNumber}
                  </p>
                </div>
                <span className="text-primary font-medium text-sm">
                  View Guide
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
